/**
 * /reset-password — public route, reached from the link in a reset mail.
 * SPEC-01 §3.7, Stage 2. Screen in ./reset-password.lazy.tsx.
 *
 * ─── THE TOKEN IS A BEARER CAPABILITY IN A URL ────────────────────────────
 *
 * `validateSearch` lives in this NON-lazy half because TanStack resolves
 * search params before the lazy component loads — the same reason
 * `routes/signup.tsx` puts it here. The token is never logged, never put in a
 * page title, and the screen replaces it out of the address bar the moment it
 * is spent.
 *
 * ─── NO REDIRECT WHEN THE TOKEN IS MISSING ────────────────────────────────
 *
 * `/signup` throws a redirect to /login when it arrives with no token. This
 * route must NOT copy that, for a reason that is about the attacker and not
 * about tidiness: arriving with no token has to look exactly like arriving
 * with a token that does not work. The screen shows one panel — "this link
 * doesn't work" — for a missing token, a token that never existed, an expired
 * one and one that has already been used. A redirect for the first of those
 * four would separate it from the other three by observation alone.
 *
 * There is deliberately no "check this token" call. The gateway offers none,
 * and it should not: a preview endpoint would be an unauthenticated,
 * unthrottled probe that tells the holder of a guessed token whether it is
 * live. The only way to learn anything about a token here is to spend it.
 *
 * No `beforeLoad` bounce for an already-authenticated visitor either. A person
 * resetting after a compromise may well have a live session — the gateway
 * revokes every one of them on success — and sending them to /boards would
 * take the door away from the one person deliberately using it.
 */
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = typeof search.token === 'string' ? search.token.trim() : '';
    return token ? { token } : {};
  },
}).lazy(() => import('./reset-password.lazy').then((d) => d.Route));
